import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Wallet, WalletAccount } from "@wallet-standard/base";
import type { AuthResponse } from "@redchain/contracts";
import { api } from "../api/client";
import { env } from "../env";
import { CustodialWalletProvider } from "./custodial/CustodialWalletProvider";
import type { GameWallet, WalletContextValue } from "./types";
import {
  connectWallet,
  disconnectWallet,
  externalGameWallet,
  lastExternalWallet,
  listExternalWallets,
  onWalletsChanged,
} from "./walletStandard";

const WalletContext = createContext<WalletContextValue | null>(null);

/** Wallet Standard / Phantom reject SIWS when the extension is on mainnet and we ask for devnet. */
function friendlyWalletError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (/chain id does not match/i.test(msg)) {
    return `This site uses Solana ${env.cluster}. Switch your wallet to ${env.cluster} (Phantom: Settings → Developer Settings → Testnet Mode / Change Network), then try again.`;
  }
  return msg;
}

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet outside WalletProvider");
  return ctx;
}

/** Wallet Standard wallets (Phantom, Solflare, and the rest). Custodial mode uses a game account. */
export function WalletProvider({ children }: { children: ReactNode }) {
  if (env.walletMode === "custodial") {
    return <CustodialWalletProvider context={WalletContext}>{children}</CustodialWalletProvider>;
  }
  return <WalletState>{children}</WalletState>;
}

function WalletState({ children }: { children: ReactNode }) {
  const [external, setExternal] = useState<{ wallet: Wallet; account: WalletAccount } | null>(null);
  const [auth, setAuth] = useState<AuthResponse | null>(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalList, setExternalList] = useState(() => listExternalWallets());
  const [restored, setRestored] = useState(false);

  useEffect(() => onWalletsChanged(() => setExternalList(listExternalWallets())), []);

  useEffect(() => {
    api.onSignedOut = () => setAuth(null);
  }, []);

  const wallet = useMemo<GameWallet | null>(() => (external ? externalGameWallet(external.wallet, external.account) : null), [external]);

  useEffect(() => {
    if (restored) return;
    setRestored(true);
    const name = lastExternalWallet();
    if (!name) return;
    void (async () => {
      try {
        const conn = await connectWallet(name, true);
        const token = await api.refresh();
        if (!token) return;
        setExternal(conn);
        const me = await api.me();
        if (me.wallet !== conn.account.address) {
          await api.logout();
          return;
        }
        setAuth({ accessToken: token, player: { registered: me.registered }, wallet: me.wallet });
      } catch {
        /* user will log in again */
      }
    })();
  }, [restored]);

  const connectExternal = useCallback(async (name: string) => {
    setAuthenticating(true);
    setError(null);
    try {
      const conn = await connectWallet(name);
      const gw = externalGameWallet(conn.wallet, conn.account);
      const res = await gw.signIn();
      setExternal(conn);
      setAuth(res);
    } catch (e) {
      setError(friendlyWalletError(e));
    } finally {
      setAuthenticating(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    if (external) await disconnectWallet(external.wallet);
    setExternal(null);
    setAuth(null);
  }, [external]);

  const value: WalletContextValue = {
    ready: true,
    wallet,
    auth,
    authenticating,
    error,
    externalWallets: externalList.map((w) => ({ name: w.name, icon: w.icon })),
    connectExternal,
    logout,
    markRegistered: () => setAuth((a) => (a ? { ...a, player: { registered: true } } : a)),
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
